import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { skip, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { PaginatorComponent } from '../../common/paginator/paginator.component';

import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { SearchHistoryService } from '../../../services/search-history/search-history.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { DocumentExportService } from '../../../services/document-export/document-export.service';
import { RestrictedAccessWarningDirective } from '../../../directives/restricted-access-warning.directive';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import {
  detectWalletChain,
  looksLikeWalletTxId,
  SUPPORTED_WALLET_CHAINS,
  WalletChainConfig,
} from '../../../utils/wallet-chain.util';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
  selector: 'app-osint-wallet-search',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    HeaderComponent,
    PaginatorComponent,
    SidebarComponent,
    RestrictedAccessWarningDirective, CreditsLabelPipe],
  templateUrl: './osint-wallet-search.component.html',
  styleUrl: './osint-wallet-search.component.css',
})
export class OsintWalletSearchComponent implements OnInit, OnDestroy {
  accessDetails: any;
  activeTab: string = 'wallet';
  availableCredits: number | null = null;
  creditsLoading = true;
  totalCredits: number = 0;

  singleWallet: string = '';

  textAreaInput: string = '';
  multipleWallets: string[] = [];
  extraWallets: Array<{ value: string }> = [];
  readonly maxSearchRows = 5;

  isMultipleSearch: boolean = false;

  allJobResultsSubscription?: Subscription;
  loadingJobIds: boolean = false;
  jobIds: string[] = [];

  private lastSearchTime: number = 0;
  private readonly SEARCH_THROTTLE_MS: number = 1000; // 1 second

  searchType: string = 'wallet';
  loadingRecentResults: boolean = false;
  recentResults: any = [];
  recentResultsPaged: any = [];

  ipAddress: string = '';
  location: string = '';

  recentResultsCount: number = 0;
  recentResultsPerPage = 5;
  recentResultsCurrentPage = 1;
  recentResultsHasPreviousPage: boolean = false;
  recentResultsHasNextPage: boolean = false;

  recentResultsSelectAll = { isSelected: false };
  isServiceDisabled: boolean = false;
  searchCreditCost: number | null = null;

  /** Inline transaction lookup when input looks like a tx hash. */
  txLookupLoading = false;
  txLookupResult: any = null;
  txLookupError: string | null = null;

  /** Click-through from an investigation: auto-run search and open the result page. */
  autoOpeningResult = false;
  private openResultAfterSearch = false;

  readonly supportedChains = SUPPORTED_WALLET_CHAINS;

  selectedFilterType: string = 'wallet';
  filterTypes: any = [
    { label: 'Wallet', value: 'wallet' },
    { label: 'Notes', value: 'notes' },
    { label: 'Date Added', value: 'date-added' },
  ];

  filterKey: string = '';
  filterValue: string = '';

  walletFilter: string = '';
  notesFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';

  get recentResultsPagedStartAndEnd() {
    if (!this.recentResults?.length || !this.recentResultsCount) {
      return { start: 0, end: 0 };
    }

    const start =
      (this.recentResultsCurrentPage - 1) * this.recentResultsPerPage + 1;
    const end = Math.min(
      start + this.recentResults.length - 1,
      this.recentResultsCount,
    );

    return { start, end };
  }

  get selectedRecentResults() {
    return this.recentResultsPaged
      .filter((recentResultPaged: any) => recentResultPaged.isSelected)
      .map((recentResultPaged: any) => recentResultPaged.osint_search_id);
  }

  get isTxIdDetected(): boolean {
    if (this.isMultipleSearch) return false;
    return looksLikeWalletTxId(this.singleWallet);
  }

  get detectedChain(): WalletChainConfig | null {
    if (this.isMultipleSearch || this.isTxIdDetected) return null;
    return detectWalletChain(this.singleWallet);
  }

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private userAccountService: UserAccountService,
    private osintSearchService: OsintSearchService,
    private searchHistoryService: SearchHistoryService,
    private searchPollingService: SearchPollingService,
    private utilsService: UtilsService,
    private documentExportService: DocumentExportService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) { }

  ngOnInit() {
    this.accessDetails = this.retrieveAccessDetails();

    // Check if OSINT service is disabled
    this.searchServiceStatusService.searchServices$.subscribe((searchServices) => {
      if (searchServices) {
        this.isServiceDisabled = !searchServices.osint;
      }
    });

    this.searchServiceStatusService.creditCosts$.subscribe((cc) => {
      this.searchCreditCost = this.searchServiceStatusService.resolveCreditCost(
        cc,
        'osintSearchCost',
      );
    });

    this.getRecentResults();
    this.getAvailableCredits();

    this.subscribeToJobResultsUpdates();

    // Prefill from navigation (e.g. counterparty / flow / timeline investigate).
    // autoSearch starts the job; openResult navigates to the result page with a loader.
    const navState = history.state;
    if (navState?.searchQuery) {
      this.singleWallet = String(navState.searchQuery).trim();
      const shouldAutoSearch = navState.autoSearch === true;
      this.openResultAfterSearch = navState.openResult === true;
      history.replaceState(
        { ...history.state, autoSearch: false, searchQuery: null, openResult: false },
        '',
      );
      if (shouldAutoSearch) {
        this.autoOpeningResult = true;
        if (looksLikeWalletTxId(this.singleWallet)) {
          this.txLookupLoading = true;
        } else {
          this.loadingJobIds = true;
        }
        setTimeout(() => {
          this.onSearchSingleWalletButtonClick();
        }, 0);
      }
    }
  }

  subscribeToJobResultsUpdates() {
    this.allJobResultsSubscription?.unsubscribe();

    this.allJobResultsSubscription = this.searchPollingService
      .getAllJobResults()
      .pipe(skip(1))
      .subscribe((map) => {
        this.recentResultsPaged.forEach((result: any) => {
          if (result.job_id) {
            const updatedStatus = map.get(result.job_id);
            if (updatedStatus) {
              result.done = updatedStatus.done || false;
              result.status = updatedStatus.status || 'pending';
            }
          }
        });
      });
  }

  ngOnDestroy() {
    this.allJobResultsSubscription?.unsubscribe();
  }

  retrieveAccessDetails() {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.accessDetails || null;
  }

  setTab(selectedTab: string) {
    this.activeTab = selectedTab;
    switch (selectedTab) {
      case 'image':
        this.router.navigate(['/osint-image-search']);
        break;
      case 'number':
        this.router.navigate(['/osint-number-search']);
        break;
      case 'email':
        this.router.navigate(['/osint-email-search']);
        break;
      case 'username':
        this.router.navigate(['/osint-username-search']);
        break;
      case 'name':
        this.router.navigate(['/osint-name-search']);
        break;
      case 'wallet':
        this.router.navigate(['/osint-wallet-search']);
        break;
    }
  }

  getAvailableCredits() {
    this.creditsLoading = true;
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const subscription = response?.data?.subscriptions?.[0] || {};
        this.availableCredits = this.userAccountService.pickAvailableCredits(response);
        this.creditsLoading = false;
        this.totalCredits = subscription?.total_credits;
      },
      error: (error) => {
        console.error(error);

        let errorMessage = 'Failed to load available credits';

        if (error?.error?.message_key) {
          errorMessage = error.error.message_key;
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.statusText) {
          errorMessage = error.statusText;
        }

        this.toastr.error(errorMessage, 'Credits Load Failed');
        this.availableCredits = 0;
        this.creditsLoading = false;
      },
    });
  }

  getRecentResults() {
    this.loadingRecentResults = true;

    this.searchHistoryService
      .getOsintHistories(
        this.searchType,
        this.recentResultsCurrentPage,
        this.recentResultsPerPage,
        this.filterKey,
        this.filterValue,
        this.startDateFilter,
        this.endDateFilter,
      )
      .subscribe({
        next: (response) => {
          this.loadingRecentResults = false;

          this.recentResults = response?.data?.data_list;
          this.recentResultsPaged = this.addAdditionalKeys(this.recentResults);

          this.recentResultsCount = response?.data?.total_items;
          this.recentResultsHasPreviousPage = response?.data?.has_previous;
          this.recentResultsHasNextPage = response?.data?.has_next;

          this.refreshPendingJobStatuses();
        },
        error: (error) => {
          console.error(error);
          this.loadingRecentResults = false;

          let errorMessage = 'Failed to load recent search results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Load Results Failed');
        },
      });
  }

  addAdditionalKeys(recentResults: any) {
    return recentResults.map((recentResult: any) => ({
      ...recentResult,
      isSelected: false,
    }));
  }

  onRecentResultPagedClick(event: Event, recentResultPaged: any) {
    const checkbox = event.target as HTMLInputElement;
    const isChecked = checkbox.checked;

    this.updateRecentResultPaged(isChecked, recentResultPaged.osint_search_id);
  }

  updateRecentResultPaged(isChecked: boolean, searchId: string) {
    const recentResultsPagedToUpdate = this.recentResultsPaged.find(
      (recentResultPaged: any) =>
        recentResultPaged.osint_search_id === searchId,
    );

    if (recentResultsPagedToUpdate) {
      recentResultsPagedToUpdate.isSelected = isChecked;
    }
  }

  onRecentResultsPageChange(page: number) {
    this.recentResultsCurrentPage = page;
    this.recentResultsSelectAll.isSelected = false;

    this.getRecentResults();
  }

  onDeleteButtonClick() {
    if (!this.selectedRecentResults.length) {
      this.toastr.warning('Please select at least one result to delete.');
      return;
    }

    this.deleteRecentResults();
  }

  deleteRecentResults() {
    this.searchHistoryService
      .deleteOsintHistories(this.selectedRecentResults)
      .subscribe({
        next: () => {
          this.recentResultsSelectAll.isSelected = false;
          this.updateRecentResultsPaged();
          this.updateRecentResultsCurrentPage();
          this.getRecentResults();
        },
        error: (error) => {
          console.error(error);

          let errorMessage = 'Failed to delete selected results';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Delete Failed');
        },
      });
  }

  updateRecentResultsPaged() {
    this.recentResultsPaged = this.recentResultsPaged.filter(
      (recentResultPaged: any) =>
        !this.selectedRecentResults.includes(recentResultPaged.osint_search_id),
    );
  }

  updateRecentResultsCurrentPage() {
    if (this.recentResultsPaged.length === 0) {
      if (this.recentResultsHasNextPage) {
        return;
      }

      if (this.recentResultsHasPreviousPage) {
        this.recentResultsCurrentPage--;
      }
    }
  }

  onFilterTypeChange() {
    this.resetFilters();
  }

  resetFilters() {
    this.recentResultsCurrentPage = 1;

    this.filterKey = '';
    this.filterValue = '';

    this.walletFilter = '';
    this.notesFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
  }

  onSearchSingleWalletButtonClick() {
    const values = this.collectWalletValues();
    if (!values.length) {
      this.clearAutoOpenState();
      this.toastr.error('Please enter a cryptocurrency address or transaction ID');
      return;
    }
    if (values.length > this.maxSearchRows) {
      this.clearAutoOpenState();
      this.toastr.error('You can only search a maximum of 5 wallets at once.');
      return;
    }

    this.singleWallet = values[0];
    if (!this.autoOpeningResult && this.isThrottled()) {
      return;
    }

    if (values.length === 1 && looksLikeWalletTxId(values[0])) {
      this.runTransactionLookup(values[0]);
      return;
    }

    const invalid = values.find((value) => !detectWalletChain(value));
    if (invalid) {
      this.clearAutoOpenState();
      this.toastr.error(
        values.length > 1
          ? 'One or more wallet addresses are unrecognized. Enter BTC, ETH, LTC, DOGE, DASH, BCH, TRX or SOL addresses.'
          : 'Unrecognized format. Enter a BTC, ETH, LTC, DOGE, DASH, BCH, TRX or SOL address — or a transaction ID (64-hex hash)',
      );
      return;
    }

    this.txLookupResult = null;
    this.txLookupError = null;
    this.isMultipleSearch = values.length > 1;
    this.multipleWallets = values;
    this.getJobIds();
  }

  get canAddSearchRow(): boolean {
    return this.extraWallets.length < this.maxSearchRows - 1;
  }

  addSearchRow(): void {
    if (!this.canAddSearchRow) {
      this.toastr.error('You can only search a maximum of 5 wallets at once.');
      return;
    }
    this.extraWallets = [...this.extraWallets, { value: '' }];
    this.isMultipleSearch = this.extraWallets.length > 0;
  }

  removeSearchRow(index: number): void {
    this.extraWallets = this.extraWallets.filter((_, i) => i !== index);
    this.isMultipleSearch = this.extraWallets.length > 0;
  }

  private collectWalletValues(): string[] {
    const rawValues = this.isMultipleSearch
      ? this.textAreaInput.split('\n')
      : [this.singleWallet];
    return rawValues
      .map((value) => String(value || '').trim().replace(/\s+/g, ''))
      .filter((value) => value.length > 0);
  }

  looksLikeTxId(input: string): boolean {
    return looksLikeWalletTxId(input);
  }

  private runTransactionLookup(hash: string): void {
    this.txLookupLoading = true;
    this.txLookupError = null;
    this.txLookupResult = null;
    this.lastSearchTime = Date.now();

    this.osintSearchService.lookupWalletTransaction(hash).subscribe({
      next: (response) => {
        this.txLookupLoading = false;
        this.autoOpeningResult = false;
        this.openResultAfterSearch = false;
        this.txLookupResult = response?.data ?? response;
        this.toastr.success('Transaction found');
      },
      error: (err) => {
        this.txLookupLoading = false;
        this.autoOpeningResult = false;
        this.openResultAfterSearch = false;
        this.txLookupError =
          err?.error?.message_key ||
          err?.error?.message ||
          'Transaction lookup failed. Please try again.';
        this.toastr.error(this.txLookupError ?? 'Transaction lookup failed.');
      },
    });
  }

  investigateFromTx(address: string): void {
    const value = String(address || '').trim();
    if (!value) return;
    this.singleWallet = value;
    this.txLookupResult = null;
    this.txLookupError = null;
    this.onSearchSingleWalletButtonClick();
  }

  fmtNative(n: number | null | undefined): string {
    if (typeof n !== 'number' || Number.isNaN(n)) return '0';
    return n.toLocaleString('en-US', { maximumFractionDigits: 8 });
  }

  shortAddr(addr: string): string {
    const value = String(addr || '');
    if (value.length <= 20) return value;
    return `${value.slice(0, 10)}…${value.slice(-8)}`;
  }

  private isThrottled(): boolean {
    const now = Date.now();
    const timeSinceLastSearch = now - this.lastSearchTime;

    return timeSinceLastSearch < this.SEARCH_THROTTLE_MS;
  }

  private clearAutoOpenState(): void {
    this.autoOpeningResult = false;
    this.openResultAfterSearch = false;
    this.loadingJobIds = false;
    this.txLookupLoading = false;
  }

  getJobIds() {
    const { ip, location } = this.retrieveIPAndLocation();
    // if (!ip) {
    //   this.toastr.error('Unable to determine IP address for the search.');
    //   return;
    // }

    const payload = {
      type: this.searchType,
      searchValues: this.multipleWallets.length
        ? this.multipleWallets.map((w) => w.trim().replace(/\s+/g, ''))
        : [this.singleWallet.trim().replace(/\s+/g, '')],
    };

    this.loadingJobIds = true;
    this.lastSearchTime = Date.now();
    const searchedWallet = payload.searchValues[0] || '';

    this.osintSearchService
      .getJobIds(payload.type, payload.searchValues, ip, location)
      .subscribe({
        next: (response) => {
          this.jobIds = this.extractJobIds(response?.data);

          this.searchPollingService.startPolling(this.jobIds, (jobIds) =>
            this.osintSearchService.getSearchResults(this.searchType, jobIds),
          );
          this.searchPollingService.ensureBackgroundPolling(
            (this.jobIds || []).map((jobId: string) => ({
              job_id: jobId,
              searchType: this.searchType,
              label: searchedWallet || jobId,
            })),
            3 * 1000,
          );
          this.getAllJobResults();

          this.resetSearchInputs();
          this.getRecentResults();

          this.loadingJobIds = false;

          if (this.openResultAfterSearch && this.jobIds?.length) {
            this.openResultAfterSearch = false;
            this.autoOpeningResult = false;
            this.goToSearchResultPage({
              job_id: this.jobIds[0],
              searched_wallet: searchedWallet,
              done: false,
              status: 'pending',
              osint_search_id: this.jobIds[0],
            });
            return;
          }

          if (this.openResultAfterSearch) {
            this.clearAutoOpenState();
            this.toastr.error('Unable to start investigation. Please try again.');
            return;
          }

          this.autoOpeningResult = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingJobIds = false;
          this.clearAutoOpenState();

          let errorMessage = 'An error occurred while processing your request';

          if (error?.error?.message_key) {
            errorMessage = error.error.message_key;
          } else if (error?.error?.message) {
            errorMessage = error.error.message;
          } else if (error?.message) {
            errorMessage = error.message;
          } else if (error?.statusText) {
            errorMessage = error.statusText;
          }

          this.toastr.error(errorMessage, 'Search Failed');
        },
      });
  }

  refreshPendingJobStatuses() {
    this.recentResultsPaged.forEach((result: any) => {
      if (result.job_id) {
        const cachedStatus = this.searchPollingService.getCachedJobStatus(
          result.job_id,
        );
        if (cachedStatus) {
          result.done = cachedStatus.done || false;
          result.status = cachedStatus.status || 'pending';
        }
      }
    });

    const pendingJobIds = this.recentResultsPaged
      .filter(
        (result: any) =>
          result.job_id &&
          (!result.done ||
            result.done === false ||
            result.status === 'pending'),
      )
      .map((result: any) => result.job_id)
      .filter((jobId: string) => !!jobId);

    if (pendingJobIds.length > 0) {
      this.searchPollingService
        .checkJobStatus(pendingJobIds, (jobIds) =>
          this.osintSearchService.getSearchResults(this.searchType, jobIds),
        )
        .subscribe({
          next: (jobResultsMap) => {
            this.recentResultsPaged.forEach((result: any) => {
              if (result.job_id) {
                const updatedStatus = jobResultsMap.get(result.job_id);
                if (updatedStatus) {
                  result.done = updatedStatus.done || false;
                  result.status = updatedStatus.status || 'pending';
                }
              }
            });
          },
          error: (error) => {
            console.warn('Failed to refresh job statuses:', error);
          },
        });
    }
  }

  getAllJobResults() { }

  extractJobIds(jobs: any[]) {
    if (!jobs || !Array.isArray(jobs)) {
      return [];
    }

    if (jobs.length > 0 && typeof jobs[0] === 'string') {
      return jobs;
    }

    return jobs.filter((job) => job.done === false).map((job) => job.job_id);
  }

  validateWallet(wallet: string) {
    const trimmedWallet = String(wallet || '').trim().replace(/\s+/g, '');
    if (!trimmedWallet) return false;
    return (
      looksLikeWalletTxId(trimmedWallet) ||
      !!detectWalletChain(trimmedWallet)
    );
  }

  extractMultipleWallets() {
    this.multipleWallets = this.textAreaInput
      .split('\n')
      .map((wallet) => wallet.trim().replace(/\s+/g, ''))
      .filter((wallet) => wallet.length > 0);
  }

  validateWallets() {
    return this.multipleWallets.every((wallet) => this.validateWallet(wallet));
  }

  onSearchMultipleWalletsButtonClick() {
    this.extractMultipleWallets();

    if (this.multipleWallets.length > 5) {
      this.toastr.error('You can search a maximum of 5 wallet addresses.');
      return;
    }

    if (!this.multipleWallets.length) {
      this.toastr.error('Please enter at least one wallet address.');
      return;
    }

    if (this.validateWallets()) {
      if (this.isThrottled()) {
        return;
      }
      this.getJobIds();
    } else {
      this.toastr.error('One or more wallet addresses are invalid');
    }
  }

  goToSearchResultPage(searchResult: any) {
    this.router.navigate(
      [`/osint-wallet-search/search-result/${searchResult.job_id}`],
      {
        state: { searchResult },
      },
    );
  }

  onRecentResultsSelectAllClick() {
    this.recentResultsSelectAll.isSelected =
      !this.recentResultsSelectAll.isSelected;

    this.recentResultsSelectAll.isSelected
      ? this.recentResultsPagedSelectAll()
      : this.recentResultsPagedDeselectAll();
  }

  recentResultsPagedSelectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: true,
      }),
    );
  }

  recentResultsPagedDeselectAll() {
    this.recentResultsPaged = this.recentResultsPaged.map(
      (recentResultPaged: any) => ({
        ...recentResultPaged,
        isSelected: false,
      }),
    );
  }

  onWalletFilterChange(wallet: string) {
    const trimmedWallet = wallet.trim();

    if (trimmedWallet === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedWallet.length === 1) {
      return;
    } else {
      this.filterKey = 'searched_wallet';
      this.filterValue = trimmedWallet;

      this.getRecentResults();
    }
  }

  onNotesFilterChange(notes: string) {
    const trimmedNotes = notes.trim();

    if (trimmedNotes === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    } else if (trimmedNotes.length === 1) {
      return;
    } else {
      this.filterKey = 'notes';
      this.filterValue = trimmedNotes;

      this.getRecentResults();
    }
  }

  onStartDateFilterChange(startDate: string) {
    this.startDateFilter = startDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onEndDateFilterChange(endDate: string) {
    this.endDateFilter = endDate;

    if (this.startDateFilter === '' && this.endDateFilter === '') {
      this.filterKey = '';
      this.filterValue = '';

      this.getRecentResults();
    }

    if (this.startDateFilter && this.endDateFilter) {
      this.filterKey = 'created_on';
      this.getRecentResults();
    }
  }

  onExportExcelButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToExcel(transformedRecentResultsPaged);
  }

  transformRecentResultsPaged() {
    return this.recentResultsPaged.map((recentResultPaged: any) => ({
      'OSINT SEARCH ID': recentResultPaged.osint_search_id,
      'JOB ID': recentResultPaged.job_id,
      'SEARCHED WALLET': recentResultPaged.searched_wallet,
      'USER EMAIL': recentResultPaged.user?.email || '',
      'IP ADDRESS': recentResultPaged.ip_address,
      'LOCATION': recentResultPaged.location,
      'DEVICE': recentResultPaged.device,
      'NOTES':
        recentResultPaged.notes === undefined ? null : recentResultPaged.notes,
      'CREATED ON': this.utilsService.formatDateTime(
        recentResultPaged.created_on,
        'MMM d, y, h:mm:ss a',
      ),
    }));
  }

  exportToExcel(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-wallet-results-${timestamp || 'export'}`;
    this.documentExportService.exportToExcel(
      transformedRecentResultsPaged,
      fileName,
      'OSINT Results',
    );
  }

  onExportCsvButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToCsv(transformedRecentResultsPaged);
  }

  exportToCsv(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-wallet-results-${timestamp || 'export'}`;
    this.documentExportService.exportToCsv(
      transformedRecentResultsPaged,
      fileName,
      'OSINT Results',
    );
  }

  onExportPdfButtonClick() {
    const transformedRecentResultsPaged = this.transformRecentResultsPaged();
    this.exportToPdf(transformedRecentResultsPaged);
  }

  exportToPdf(transformedRecentResultsPaged: any[]) {
    if (!transformedRecentResultsPaged.length) {
      this.toastr.info('No recent results to export');
      return;
    }

    const timestamp = this.utilsService.formatDateTime(
      new Date().toISOString(),
      'dd-MM-yyyy_HH-mm-ss',
    );

    const fileName = `osint-wallet-results-${timestamp || 'export'}`;
    const title = `OSINT results generated from Ryno`;
    const username = this.getUsername();

    this.documentExportService.exportResultsToPdf(
      transformedRecentResultsPaged,
      fileName,
      title,
      username,
    );
  }

  private getUsername(): string {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    return userPortal?.loginDetails?.user_name || '';
  }

  onMultipleSearchChange(): void {
    if (this.isMultipleSearch) {
      if (!this.textAreaInput.trim() && this.singleWallet.trim()) {
        this.textAreaInput = this.singleWallet.trim();
      }
      return;
    }
    if (!this.singleWallet.trim()) {
      const first = this.textAreaInput
        .split('\n')
        .map((value) => value.trim())
        .find((value) => value.length > 0);
      if (first) {
        this.singleWallet = first;
      }
    }
  }

  resetSearchInputs() {
    this.singleWallet = '';
    this.textAreaInput = '';
    this.multipleWallets = [];
    this.extraWallets = [];
    this.isMultipleSearch = false;
  }

  private retrieveIPAndLocation(): { ip: string; location: string } {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;

    const ipRaw = userPortal?.ipDetails?.ip || '';
    const location = userPortal?.ipDetails?.city || '';

    const ip = ipRaw.trim();
    const isValidIPv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(ip);

    return {
      ip: isValidIPv4 ? ip : '',
      location,
    };
  }

  onBuyCreditsButtonClick() {
    this.router.navigate(['/plan-pricing']);
  }

  /** Truncate text to max words; if longer, append " ....." */
  truncateToWords(text: string | null | undefined, maxWords: number = 20): string {
    if (text == null || text === '') return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + ' .....';
  }
}
